import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { siteConfig } from '../../config/site';
import Button from '../ui/Button';

const Navbar: React.FC = React.memo(() => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const [applyDropdownOpen, setApplyDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const applyDropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const img = new Image();
    img.onload = () => setLogoLoaded(true);
    img.src = '/enhance_companies_logo.png';
    img.fetchPriority = 'high';
  }, []);

  const handleMeetUsClick = React.useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleNavClick = React.useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setCompanyDropdownOpen(false);
      }
      if (applyDropdownRef.current && !applyDropdownRef.current.contains(event.target as Node)) {
        setApplyDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setCompanyDropdownOpen(false);
    setApplyDropdownOpen(false);
  }, [location.pathname]);

  const isApplyActive = () => {
    return location.pathname === '/apply' || location.pathname === '/for-agencies';
  };

  const isCompanyActive = () => {
    return location.pathname === '/about' || location.pathname === '/contact' || location.pathname === '/services/contact';
  };

  const getCompanyLabel = () => {
    if (location.pathname === '/about') return 'About';
    if (location.pathname === '/contact') return 'Contact';
    if (location.pathname === '/services/contact') return 'Services Contact';
    return 'Company';
  };

  const isActive = (path: string) => {
    if (path === '/access-store') {
      return location.pathname === '/login' || location.pathname === '/dashboard' || location.pathname.startsWith('/product/');
    }
    if (path === '/store') {
      return location.pathname === '/store' || location.pathname.startsWith('/store/');
    }
    return location.pathname === path;
  };

  const isDarkPage = location.pathname.startsWith('/store');

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled ? 'py-0 md:py-3' : 'py-0 md:py-2'
    }`}>
      <nav className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-500 ${
        isScrolled
          ? isDarkPage
            ? 'bg-black/65 backdrop-blur-xl rounded-none md:rounded-2xl'
            : 'bg-white/80 backdrop-blur-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] rounded-none md:rounded-2xl border-0 md:border md:border-white/60'
          : isDarkPage
            ? 'bg-black/40 backdrop-blur-md md:bg-transparent md:backdrop-blur-0 rounded-none md:rounded-2xl'
            : 'bg-transparent'
      }`}>
        <div className={`flex items-center justify-between transition-all duration-300 ${
          isScrolled ? 'h-14' : 'h-16'
        }`}>
          {/* Logo / Wordmark - Left */}
          {isDarkPage ? (
            <Link
              to="/"
              className="flex items-center flex-shrink-0 group"
              aria-label="Enhance Companies home page"
            >
              <span className="text-white text-[11px] sm:text-xs font-bold uppercase tracking-[0.32em] group-hover:text-white/80 transition-colors">
                Enhance Companies
              </span>
              <span className="hidden sm:inline-block w-6 h-px bg-white/30 mx-3 transition-all duration-500 group-hover:w-10 group-hover:bg-white/60" />
              <span className="hidden sm:inline-flex items-center gap-2 text-white/60 text-[10px] uppercase tracking-[0.32em] group-hover:text-white transition-colors">
                <span className="inline-block transition-transform duration-500 group-hover:-translate-x-1">←</span>
                Back Home
              </span>
            </Link>
          ) : (
            <Link to="/" className="flex items-center relative flex-shrink-0" aria-label="Enhance Companies home page">
              <div className="relative">
                {logoLoaded ? (
                  <img
                    src="/enhance_companies_logo.png"
                    alt="Enhance Companies Logo"
                    className={`w-auto transition-all duration-300 ${
                      isScrolled ? 'h-8 sm:h-12' : 'h-10 sm:h-16'
                    }`}
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    width="360"
                    height="72"
                  />
                ) : (
                  <div
                    className={`rounded-lg flex items-center justify-center transition-all duration-300 ${
                      isScrolled ? 'h-8 sm:h-12' : 'h-10 sm:h-16'
                    }`}
                    style={{ width: '200px' }}
                  >
                    <img
                      src="/enhance_companies_logo.png"
                      alt="Enhance Companies Logo"
                      className="w-auto h-full object-contain"
                    />
                  </div>
                )}
              </div>
            </Link>
          )}

          {/* Desktop Navigation - Centered (hidden on store) */}
          {!isDarkPage && (
          <nav className="hidden md:flex items-center absolute left-1/2 -translate-x-1/2" role="navigation" aria-label="Main navigation">
            <div className={`flex items-center gap-1 rounded-full px-2.5 py-1.5 transition-all duration-300 ${
              isDarkPage
                ? isScrolled
                  ? 'bg-white/5 border border-white/15'
                  : 'bg-white/[0.04] border border-white/10'
                : isScrolled
                ? 'bg-white/90 border border-white/80 shadow-sm'
                : 'bg-white/70 border border-white/60'
            }`}>
              {siteConfig.navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => {
                    setIsOpen(false);
                    handleNavClick();
                  }}
                  className={`px-3 py-1.5 text-xs font-semibold transition-all duration-200 rounded-full ${
                    isActive(item.href)
                      ? isDarkPage
                        ? 'bg-white text-black shadow-md'
                        : 'bg-navy-700 text-white shadow-md'
                      : isDarkPage
                        ? 'text-white/80 hover:text-white hover:bg-white/10'
                        : 'text-neutral-gray-700 hover:text-navy-700 hover:bg-white/80'
                  }`}
                  aria-current={isActive(item.href) ? 'page' : undefined}
                >
                  {item.name}
                </Link>
              ))}

              {/* Company Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setCompanyDropdownOpen(!companyDropdownOpen)}
                  className={`px-3 py-1.5 text-xs font-semibold transition-all duration-200 rounded-full flex items-center gap-1 ${
                    isCompanyActive()
                      ? isDarkPage
                        ? 'bg-white text-black shadow-md'
                        : 'bg-navy-700 text-white shadow-md'
                      : isDarkPage
                        ? 'text-white/80 hover:text-white hover:bg-white/10'
                        : 'text-neutral-gray-700 hover:text-navy-700 hover:bg-white/80'
                  }`}
                >
                  {getCompanyLabel()}
                  <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${companyDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Dropdown Menu */}
                {companyDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-36 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50">
                    {siteConfig.companyDropdown.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => {
                          setCompanyDropdownOpen(false);
                          handleNavClick();
                        }}
                        className={`block px-4 py-2 text-xs font-semibold transition-all duration-200 ${
                          isActive(item.href)
                            ? 'bg-navy-50 text-navy-700'
                            : 'text-neutral-gray-700 hover:text-navy-700 hover:bg-slate-50'
                        }`}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </nav>
          )}

          {/* CTA Buttons - Right */}
          <div className="hidden md:flex items-center gap-2.5 flex-shrink-0">
            {!isDarkPage && (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  as={Link}
                  to="/agent/login"
                  onClick={handleNavClick}
                  className={`text-xs px-4 py-2 border rounded-full transition-all duration-300 font-semibold ${
                    isScrolled
                      ? 'text-navy-800 border-navy-400/60 hover:border-navy-600 hover:bg-white/90 hover:shadow-lg'
                      : 'text-navy-800 border-navy-300/60 hover:border-navy-500/60 hover:bg-white/70 hover:shadow-md'
                  }`}
                  aria-label="Agent sign in"
                >
                  Agent Sign In
                </Button>

                {/* Store link */}
                <Link
                  to="/store"
                  onClick={handleNavClick}
                  className="group relative text-[11px] font-bold uppercase tracking-[0.2em] px-4 py-2 rounded-full transition-all duration-300 text-navy-800 hover:text-navy-900"
                  aria-label="Shop the Enhance Companies store"
                >
                  Shop
                  <span
                    className={`pointer-events-none absolute left-4 right-4 -bottom-0.5 h-px transition-transform duration-500 origin-left bg-navy-800 ${
                      isActive('/store') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                    }`}
                  />
                </Link>
              </>
            )}

            {/* Apply Now Dropdown */}
            <div className="relative" ref={applyDropdownRef}>
              <button
                onClick={() => setApplyDropdownOpen(!applyDropdownOpen)}
                className={`rounded-full transition-all duration-500 flex items-center font-bold ${
                  isDarkPage
                    ? 'bg-white text-black hover:bg-neutral-gray-100 border border-white text-[10px] uppercase tracking-[0.3em] hover:tracking-[0.35em] px-5 py-2.5 gap-3'
                    : `text-xs px-4 py-2 gap-1.5 shadow-md ${
                        isApplyActive()
                          ? 'bg-navy-800 text-white'
                          : 'bg-navy-700 text-white hover:bg-navy-800'
                      } ${isScrolled ? 'hover:shadow-xl hover:scale-105' : 'hover:shadow-lg hover:scale-105'}`
                }`}
              >
                Apply Now
                <ChevronDown className={`transition-transform duration-500 ${
                  isDarkPage ? 'w-3 h-3 opacity-70' : 'w-3.5 h-3.5'
                } ${applyDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {applyDropdownOpen && (
                <div
                  className={`absolute top-full right-0 mt-3 w-72 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.35)] py-2.5 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 ${
                    isDarkPage
                      ? 'bg-black/95 backdrop-blur-xl border border-white/15'
                      : 'bg-white border border-slate-200'
                  }`}
                >
                  <p
                    className={`px-5 pt-3 pb-2.5 text-[10px] font-semibold uppercase tracking-[0.3em] ${
                      isDarkPage ? 'text-white/50' : 'text-neutral-gray-500'
                    }`}
                  >
                    I am applying as
                  </p>

                  <Link
                    to="/apply"
                    onClick={() => {
                      setApplyDropdownOpen(false);
                      handleNavClick();
                    }}
                    className={`group flex items-start gap-4 px-5 py-3.5 transition-all duration-300 ${
                      isDarkPage
                        ? 'hover:bg-white/5'
                        : 'hover:bg-slate-50'
                    } ${
                      isActive('/apply')
                        ? isDarkPage ? 'bg-white/5' : 'bg-navy-50/60'
                        : ''
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-bold uppercase tracking-[0.12em] ${
                          isDarkPage ? 'text-white' : 'text-navy-800'
                        }`}
                      >
                        An Agent
                      </p>
                      <p
                        className={`mt-1 text-xs leading-snug ${
                          isDarkPage ? 'text-white/55' : 'text-neutral-gray-500'
                        }`}
                      >
                        Join our network of producers.
                      </p>
                    </div>
                    <span
                      className={`flex-shrink-0 text-base transition-transform duration-500 group-hover:translate-x-1 ${
                        isDarkPage ? 'text-white/70' : 'text-navy-700'
                      }`}
                    >
                      →
                    </span>
                  </Link>

                  <div className={`mx-5 my-1 h-px ${isDarkPage ? 'bg-white/10' : 'bg-slate-200'}`} />

                  <Link
                    to="/for-agencies"
                    onClick={() => {
                      setApplyDropdownOpen(false);
                      handleMeetUsClick();
                    }}
                    className={`group flex items-start gap-4 px-5 py-3.5 transition-all duration-300 ${
                      isDarkPage
                        ? 'hover:bg-white/5'
                        : 'hover:bg-slate-50'
                    } ${
                      isActive('/for-agencies')
                        ? isDarkPage ? 'bg-white/5' : 'bg-navy-50/60'
                        : ''
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-bold uppercase tracking-[0.12em] ${
                          isDarkPage ? 'text-white' : 'text-navy-800'
                        }`}
                      >
                        An Agency
                      </p>
                      <p
                        className={`mt-1 text-xs leading-snug ${
                          isDarkPage ? 'text-white/55' : 'text-neutral-gray-500'
                        }`}
                      >
                        Partner with us as a team.
                      </p>
                    </div>
                    <span
                      className={`flex-shrink-0 text-base transition-transform duration-500 group-hover:translate-x-1 ${
                        isDarkPage ? 'text-white/70' : 'text-navy-700'
                      }`}
                    >
                      →
                    </span>
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`p-2 rounded-lg transition-all duration-300 ${
                isDarkPage
                  ? 'text-white hover:bg-white/10'
                  : isScrolled
                  ? 'text-neutral-gray-700 hover:text-navy-700 hover:bg-white/80'
                  : 'text-neutral-gray-600 hover:text-navy-600 hover:bg-white/60'
              }`}
              aria-label="Toggle menu"
            >
              <div className="relative w-6 h-6">
                <X 
                  className={`h-6 w-6 absolute inset-0 transition-all duration-300 ${
                    isOpen 
                      ? 'opacity-100 rotate-0 scale-100' 
                      : 'opacity-0 rotate-90 scale-0'
                  }`} 
                />
                <Menu 
                  className={`h-6 w-6 absolute inset-0 transition-all duration-300 ${
                    isOpen 
                      ? 'opacity-0 -rotate-90 scale-0' 
                      : 'opacity-100 rotate-0 scale-100'
                  }`} 
                />
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className={`md:hidden absolute top-full left-0 right-0 shadow-lg transition-all duration-300 ${
            isDarkPage
              ? 'bg-black/95 backdrop-blur-xl border-t border-white/10'
              : isScrolled
              ? 'mt-2 mx-4 rounded-2xl bg-white/95 backdrop-blur-xl border border-white/60'
              : 'bg-white/90 backdrop-blur-md border-t border-white/60'
          }`}>
            <div className="px-4 py-4 space-y-3">
              {siteConfig.navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => {
                    setIsOpen(false);
                    handleNavClick();
                  }}
                  className={`block px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive(item.href)
                      ? isDarkPage
                        ? 'bg-white/10 text-white'
                        : 'bg-navy-100 text-navy-700'
                      : isDarkPage
                        ? 'text-white/85 hover:text-white hover:bg-white/5'
                        : 'text-neutral-gray-600 hover:text-navy-600 hover:bg-white/50 hover:shadow-[0_0_15px_rgba(79,70,229,0.2)] hover:scale-105'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              {/* Company section in mobile */}
              {siteConfig.companyDropdown.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => {
                    setIsOpen(false);
                    handleNavClick();
                  }}
                  className={`block px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive(item.href)
                      ? isDarkPage
                        ? 'bg-white/10 text-white'
                        : 'bg-navy-100 text-navy-700'
                      : isDarkPage
                        ? 'text-white/85 hover:text-white hover:bg-white/5'
                        : 'text-neutral-gray-600 hover:text-navy-600 hover:bg-white/50 hover:shadow-[0_0_15px_rgba(79,70,229,0.2)] hover:scale-105'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              {/* Store link - mobile */}
              <Link
                to="/store"
                onClick={() => {
                  setIsOpen(false);
                  handleNavClick();
                }}
                className={`block px-3 py-2 rounded-lg text-sm font-bold uppercase tracking-wider transition-all duration-200 ${
                  isActive('/store')
                    ? isDarkPage
                      ? 'bg-white/10 text-white'
                      : 'bg-navy-100 text-navy-700'
                    : isDarkPage
                      ? 'text-white hover:bg-white/5'
                      : 'text-neutral-gray-700 hover:text-navy-700 hover:bg-white/50'
                }`}
              >
                Shop
              </Link>
              <div className={`pt-3 border-t space-y-3 ${
                isDarkPage ? 'border-white/10' : 'border-white/60'
              }`}>
                {/* Apply Now Section */}
                <p className={`px-3 text-xs font-semibold uppercase tracking-wider ${
                  isDarkPage ? 'text-white/50' : 'text-slate-500'
                }`}>Apply Now</p>
                {isDarkPage ? (
                  <>
                    <Link
                      to="/apply"
                      onClick={() => { setIsOpen(false); handleNavClick(); }}
                      className="block w-full text-center px-4 py-2.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-sm font-semibold uppercase tracking-wider hover:bg-white/20 hover:border-white/40 transition-all duration-300"
                    >
                      Agents
                    </Link>
                    <Link
                      to="/for-agencies"
                      onClick={() => { setIsOpen(false); handleMeetUsClick(); }}
                      className="block w-full text-center px-4 py-2.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-sm font-semibold uppercase tracking-wider hover:bg-white/20 hover:border-white/40 transition-all duration-300"
                    >
                      Agencies
                    </Link>
                    <Link
                      to="/agent/login"
                      onClick={() => { setIsOpen(false); handleNavClick(); }}
                      className="block w-full text-center px-4 py-2.5 rounded-full bg-transparent border border-white/30 text-white text-sm font-semibold uppercase tracking-wider hover:bg-white/5 hover:border-white/60 transition-all duration-300 mt-2"
                    >
                      Agent Sign In
                    </Link>
                  </>
                ) : (
                  <>
                    <div className="w-full">
                      <Button
                        size="sm"
                        variant="primary"
                        className="w-full block text-center hover:shadow-[0_0_15px_rgba(79,70,229,0.3)] hover:scale-105 transition-all duration-200"
                        as={Link}
                        to="/apply"
                        onClick={() => {
                          setIsOpen(false);
                          handleNavClick();
                        }}
                      >
                        Agents
                      </Button>
                    </div>
                    <div className="w-full">
                      <Button
                        size="sm"
                        variant="primary"
                        className="w-full block text-center hover:shadow-[0_0_15px_rgba(79,70,229,0.3)] hover:scale-105 transition-all duration-200"
                        as={Link}
                        to="/for-agencies"
                        onClick={() => {
                          setIsOpen(false);
                          handleMeetUsClick();
                        }}
                      >
                        Agencies
                      </Button>
                    </div>
                    <div className="w-full pt-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="w-full block text-center border border-navy-300 hover:border-navy-500 transition-all duration-200"
                        as={Link}
                        to="/agent/login"
                        onClick={() => {
                          setIsOpen(false);
                          handleNavClick();
                        }}
                      >
                        Agent Sign In
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
});

Navbar.displayName = 'Navbar';

export default Navbar;